import type { MnaMatrix } from './mna';

export type PropValue = number | string | boolean;
export type Props = Record<string, PropValue>;

export interface Pin {
  x: number;
  y: number;
}

/** 3핀 이상 소자의 핀 배치 (격자 단위). t = 축 방향 거리('end' = 끝점), s = 수직 방향 오프셋 */
export interface PinSlot {
  t: number | 'end';
  s: number;
  label: string;
}

/** 발열 모델: 열저항(℃/W), 열용량(J/℃), 타버리는 온도(℃, 없으면 안 탐) */
export interface ThermalSpec {
  rth: number;
  cth: number;
  tmax?: number;
}

/** 축 정렬된 소자의 로컬 좌표계 */
export interface Axis {
  ux: number;
  uy: number;
  vx: number;
  vy: number;
  len: number;
  flip: number;
}

/** 모든 회로 소자의 기반 클래스. 격자 좌표 (x1,y1)-(x2,y2) 선분으로 표현된다. */
export abstract class Element {
  id: string;
  readonly type: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  props: Props;

  /** 넷리스트 빌드 후 채워지는 핀별 노드 인덱스 (-1 = 접지) */
  nodes: number[] = [];
  /** 전압원 가지 인덱스 (필요한 소자만) */
  vsIndex = -1;

  /** 표시용: pin0 -> 소자 내부 -> pin1 방향 전류 (A) */
  current = 0;
  /** 표시용: V(pin0) - V(pin1) */
  voltageDrop = 0;

  /** 적응형 스텝: 이번 스텝의 오차 ÷ 허용 오차 (1 을 넘으면 스텝을 다시 한다) */
  errRatio = 0;
  /** 소자 온도 (℃) */
  temp = 25;
  /** 과열로 망가졌는가 */
  burnt = false;

  constructor(id: string, type: string, x1: number, y1: number, x2: number, y2: number, props: Props) {
    this.id = id;
    this.type = type;
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
    this.props = props;
  }

  get pinCount(): number {
    return 2;
  }

  /** 핀 위치를 pinLayout() 으로 정하는 소자인가 (3핀 이상, 모듈 인스턴스) */
  get usesLayout(): boolean {
    return this.pinCount > 2;
  }

  get pins(): Pin[] {
    if (!this.usesLayout && this.pinCount === 1) return [{ x: this.x1, y: this.y1 }];
    if (!this.usesLayout) {
      return [
        { x: this.x1, y: this.y1 },
        { x: this.x2, y: this.y2 },
      ];
    }
    const a = this.axis();
    return this.pinLayout().map((slot) => {
      const t = slot.t === 'end' ? a.len : slot.t;
      const s = slot.s * a.flip;
      return { x: this.x1 + a.ux * t + a.vx * s, y: this.y1 + a.uy * t + a.vy * s };
    });
  }

  /** 3핀 이상 소자가 오버라이드: 핀 배치 */
  pinLayout(): PinSlot[] {
    return [];
  }

  /** 축 정렬 로컬 좌표 (x1,y1 에서 x2,y2 방향이 u, 그 수직이 v) */
  axis(): Axis {
    const dx = this.x2 - this.x1;
    const dy = this.y2 - this.y1;
    const horizontal = Math.abs(dx) >= Math.abs(dy);
    const ux = horizontal ? Math.sign(dx) || 1 : 0;
    const uy = horizontal ? 0 : Math.sign(dy) || 1;
    return {
      ux, uy,
      vx: -uy, vy: ux,
      len: Math.max(1, horizontal ? Math.abs(dx) : Math.abs(dy)),
      flip: this.bool('flip', false) ? -1 : 1,
    };
  }

  /** 각 핀으로 소자에 '들어가는' 전류 (A). 도선 전류 복원에 쓰인다 */
  pinCurrents(): number[] {
    if (this.pinCount === 1) return [0];
    return [this.current, -this.current];
  }

  /** MNA에서 추가 미지수(가지 전류)가 필요한 개수 */
  get voltageSourceCount(): number {
    return 0;
  }

  /** 뉴턴-랩슨 반복이 필요한 비선형 소자인가 */
  get nonlinear(): boolean {
    return false;
  }

  /** 저항 0으로 두 노드를 합쳐버리는 소자인가 (도선, 닫힌 스위치, 이상 전류계) */
  get idealConductor(): boolean {
    return false;
  }

  get isGround(): boolean {
    return false;
  }

  /** 접지를 핀 없이 기준으로 쓰는 소자 (로직, 연산증폭기 출력) */
  get usesImplicitGround(): boolean {
    return false;
  }

  num(key: string, def = 0): number {
    const v = this.props[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : def;
  }

  str(key: string, def = ''): string {
    const v = this.props[key];
    return typeof v === 'string' ? v : def;
  }

  bool(key: string, def = false): boolean {
    const v = this.props[key];
    return typeof v === 'boolean' ? v : def;
  }

  /** 시뮬레이션 초기화 시 내부 상태 리셋 */
  resetState(): void {}

  /** 타임스텝 시작 — 동반 모델(companion model) 갱신 */
  beginStep(_dt: number, _time: number): void {}

  /** 뉴턴 반복마다 호출되어 MNA 행렬에 스탬프 */
  stamp(_m: MnaMatrix): void {}

  /**
   * 비선형 소자용 뉴턴-랩슨 훅. 방금 푼 해를 읽어 동작점을 갱신하고
   * 직전 동작점과의 변화량(수렴 판정용)을 돌려준다.
   */
  updateNewton(_m: MnaMatrix): number {
    return 0;
  }

  /** 해를 읽어 내부 상태와 표시값 갱신 */
  endStep(m: MnaMatrix, dt: number): void {
    const a = this.nodes[0] ?? -1;
    const b = this.pinCount > 1 ? this.nodes[1] ?? -1 : -1;
    this.voltageDrop = m.nodeVoltage(a) - m.nodeVoltage(b);
    this.current = this.computeCurrent(m, dt);
  }

  protected computeCurrent(_m: MnaMatrix, _dt: number): number {
    return 0;
  }

  /** 이 소자가 허용하는 최대 시간 간격 (초). 빠르게 변하는 소자가 줄인다 */
  get maxStep(): number {
    return Infinity;
  }

  /** 발열 모델 (없으면 온도 계산 안 함) */
  thermalSpec(): ThermalSpec | null {
    return null;
  }

  /** 열로 바뀌는 전력 (W). 기본은 소비 전력 */
  get heat(): number {
    const p = this.power;
    return Number.isFinite(p) && p > 0 ? p : 0;
  }

  /** 과열로 망가질 때 한 번 호출 */
  protected onBurn(): void {}

  /**
   * 스텝이 확정된 뒤에만 호출된다 (되돌릴 수 없는 일: 발열 누적, MCU 코드 실행 등).
   * 기본 구현은 1차 열 모델:  C·dT/dt = P − (T − T주변)/R
   */
  afterAccept(dt: number, ambient: number): void {
    const th = this.thermalSpec();
    if (!th) return;
    const tInf = ambient + this.heat * th.rth;
    const tau = Math.max(th.rth * th.cth, 1e-6);
    this.temp = tInf + (this.temp - tInf) * Math.exp(-dt / tau);
    if (th.tmax !== undefined && !this.burnt && this.temp > th.tmax) {
      this.burnt = true;
      this.onBurn();
    }
  }

  resetThermal(ambient: number): void {
    this.temp = ambient;
    this.burnt = false;
  }

  /** 적응형 스텝에서 되돌리기 위한 상태 저장 (원시값 필드만) */
  snapshot(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(this)) {
      const v = (this as unknown as Record<string, unknown>)[k];
      const t = typeof v;
      if (t === 'number' || t === 'string' || t === 'boolean') out[k] = v;
    }
    return out;
  }

  restore(s: Record<string, unknown>): void {
    Object.assign(this, s);
  }

  /** 소비/공급 전력 (W). 양수면 소비 */
  get power(): number {
    return this.voltageDrop * this.current;
  }

  /** 클릭했을 때 보여줄 정보 줄 */
  abstract info(): string[];
}
